import { ReactElement, useEffect, useState } from 'react';
import { Button, Card, Col, Form, Input, InputNumber, Row, Select, Space, Spin, Table, TableColumnsType } from 'antd';
import { useTranslation } from 'react-i18next';
import { ApplicationType, EndingType, FeeBasis, GetFeeUnit, GetTrailFeeDto, PropertyPurpose } from '@pmp/api';
import styles from '../styles.module.css';
import { usePmpFetch } from '@pmp/hooks';

const formatGbp = (n: number) => '£' + n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const calculateFeeBasedOnLoanAmount = (loanAmount: number, feeValue: number) => {
  return loanAmount * feeValue;
};

export const ProcurationFeeCalculator = (): ReactElement => {
  const [isFetchingFirms, , data, , fetchData] = usePmpFetch('lenderFirmsGetAllLenderFirms');
  const [isFetchingPaymentsRoutes, , firmById, , fetchFirmById] = usePmpFetch('lenderFirmsGetLenderFirm');
  const [isCalculating, , calcResult, , calculate] = usePmpFetch('procurationFeeCalculatorCalculate');
  const { t } = useTranslation();
  const [calculated, setCalculated] = useState(false);
  const [form] = Form.useForm();
  const selectedFirmId = Form.useWatch('firm', form);
  const loanAmount = Form.useWatch('loan', form);

  useEffect(() => {
    fetchData();
  }, [fetchData]);


  useEffect(() => {
    if (selectedFirmId) {
      fetchFirmById({ path: { firmId: selectedFirmId } });
    }
  }, [selectedFirmId, fetchFirmById]);

  const firms = data ?? [];

  const submissionRouteIds = selectedFirmId
    ? [
        ...new Set(
          [firmById?.payments?.defaultPaymentRouteFirmId, ...(firmById?.payments?.paymentRouteFirmIds ?? [])].filter(Boolean) as string[],
        ),
      ]
    : [];
  const submissionRouteOptions = submissionRouteIds
    .map(id => firms.find(f => String(f.id) === id))
    .filter(Boolean)
    .map(firm => ({ label: firm!.name, value: firm!.ohpenId }));

  const applicationTypeOptions = Object.values(ApplicationType).map(v => ({
    label: t(`lender.procurationFees.labels.applicationTypeOptions.${v}`),
    value: v,
  }));

  const propertyPurposeOptions = Object.values(PropertyPurpose).map(v => ({
    label: t(`lender.procurationFees.labels.propertyPurposeOptions.${v}`),
    value: v,
  }));

  const pipelineSteps = [
    {
      key: 'firm',
      label: t('lender.procurationFees.labels.ownerFirm'),
      placeholder: t('lender.procurationFees.labels.ownerFirmPlaceholder'),
      required: true,
      options: firms.map(firm => ({ label: firm.name, value: firm.id})),
    },
    {
      key: 'submissionRoute',
      required: true,
      label: t('lender.procurationFees.labels.paymentRoute'),
      placeholder: selectedFirmId
        ? t('lender.procurationFees.labels.paymentRoutePlaceholder')
        : t('lender.procurationFees.labels.paymentRouteNoFirmPlaceholder'),
      options: submissionRouteOptions,
      isDisabled: !selectedFirmId,
    },
    {
      key: 'scope',
      fields: [
        {
          key: 'applicationType',
          label: t('lender.procurationFees.labels.applicationType'),
          options: applicationTypeOptions as { label: string; value: string }[],
          required: true,
        },
        {
          key: 'propertyPurpose',
          label: t('lender.procurationFees.labels.propertyPurpose'),
          options: propertyPurposeOptions as { label: string; value: string }[],
        },
      ],
    },
    {
      key: 'packager',
      label: t('lender.procurationFees.labels.packagerFirm'),
      placeholder: t('lender.procurationFees.labels.packagerFirmPlaceholder'),
      options: (firmById?.packaging?.packagerFirmIds ?? [])
          .map(id => firms.find(f => String(f.id) === id))
          .filter(Boolean)
          .map(firm => ({ label: firm!.name, value: firm!.id })),
    },
  ];

  const handleCalculate = () => {
    form
      .validateFields()
      .then(values => {
        calculate({
          body: {
            ownerFirmId: values.firm,
            submissionRouteId: values.submissionRoute,
            packagerFirmId: values.packager,
            ...(values.applicationType !== undefined && { applicationType: values.applicationType }),
            ...(values.propertyPurpose !== undefined && { propertyPurpose: values.propertyPurpose }),
          },
        });
        setCalculated(true);
        if (values.loan && calcResult?.completionFee?.value && calcResult?.completionFee?.unit === 'BasisPoints') {
          calculateFeeBasedOnLoanAmount(values.loan, calcResult?.completionFee?.value);
        }
      })
      .catch(() => undefined);
  };

  const finalFee = (value: number | undefined, unit: GetFeeUnit | undefined): string => {
    if (value === undefined) return '—';
    return unit === GetFeeUnit.CURRENCY ? formatGbp(value) : `${(value / 100)}%`;
  };

  const dash = (v: number | undefined) => (v == null ? '—' : v);

  const trailFeeColumns: TableColumnsType<GetTrailFeeDto> = [
    {
      title: t('lender.procurationFees.labels.value'),
      dataIndex: 'value',
      render: (_, row) => finalFee(row.value, row.unit),
    },
    {
      title: t('lender.procurationFees.labels.basis'),
      dataIndex: 'basis',
      render: (basis: FeeBasis | null | undefined) => (basis ? t(`lender.procurationFees.labels.basisOptions.${basis}`) : '—'),
    },
    {
      title: t('lender.procurationFees.labels.startingMonth'),
      dataIndex: 'startingMonth',
      render: dash,
    },
    {
      title: t('lender.procurationFees.labels.periodInMonths'),
      dataIndex: 'intervalInMonths',
      render: dash,
    },
    {
      title: t('lender.procurationFees.labels.endingType'),
      dataIndex: 'endingType',
      render: (endingType: EndingType) => t(`lender.procurationFees.labels.endingTypeOptions.${endingType}`),
    },
  ];

  const totalClass = [styles.total, calculated && styles.totalActive].filter(Boolean).join(' ');

  const isLoading = isFetchingFirms || isFetchingPaymentsRoutes || isCalculating;

  return (
    <Card styles={{ body: { padding: 0 } }} className={styles.calculator}>
      <Spin spinning={isLoading}>
        <Form
          form={form}
          layout="vertical"
          onValuesChange={changed => {
            setCalculated(false);
            if ('firm' in changed) {
              form.setFieldValue('submissionRoute', undefined);
            }
          }}
        >
          <section className={styles.scope}>
            <div className={styles.scopeField}>
              <Form.Item label={<>{t('lender.procurationFees.labels.loanAmount')}</>} style={{ marginBottom: 0 }}>
                <Space.Compact>
                  <Input className={styles.currencyPrefix} defaultValue="£" disabled />
                  <Form.Item name="loan" initialValue={null} noStyle>
                    <InputNumber<number>
                      formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={v => Number(v?.replace(/,/g, '') || 0)}
                      min={1}
                      precision={0}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Space.Compact>
              </Form.Item>
            </div>
          </section>

          <div className={styles.sectionRule} />

          <section className={styles.pipeline}>
            {pipelineSteps.map((step, i) => {
              const isFirst = i === 0;
              const isLast = i === pipelineSteps.length - 1;
              return (
                <div key={step.key} className={styles.step}>
                  <div className={styles.rail}>
                    <div
                      className={[styles.railLine, isFirst && styles.railLineFirst, isLast && styles.railLineLast]
                        .filter(Boolean)
                        .join(' ')}
                    />
                    <div className={styles.node}>{i + 1}</div>
                  </div>
                  {'fields' in step && step.fields != null ? (
                    <div className={styles.stepBodyRow}>
                      {step.fields.map(field => (
                        <Form.Item
                          key={field.key}
                          name={field.key}
                          label={<>{field.label}</>}
                          style={{ marginBottom: 0, flex: 1 }}
                          rules={field.required ? [{ required: true, message: t('global.errors.required') }] : []}
                        >
                          <Select options={field.options} allowClear style={{ width: '100%' }} />
                        </Form.Item>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.stepBody}>
                      <Form.Item
                        name={step.key}
                        label={<>{step.label}</>}
                        style={{ marginBottom: 0 }}
                        rules={step.required ? [{ required: true, message: t('global.errors.required') }] : []}
                      >
                        <Select
                          options={step.options}
                          placeholder={step.placeholder}
                          allowClear
                          showSearch
                          disabled={step.isDisabled || !step.options.length}
                          optionFilterProp="label"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        </Form>

        <section className={totalClass}>
          <Row justify={'space-between'}>
            <Col>
              <span style={{ fontWeight: 500 }}>{t('lender.procurationFees.labels.completionFee')}</span>
              <div className={styles.totalSub}>
                {calculated
                  ? calcResult?.isDefault
                    ? t('lender.procurationFees.labels.isDefault')
                    : calcResult?.feeName
                  : t('lender.procurationFees.labels.chooseOptions')}
              </div>
            </Col>
            <Col>
              <span style={{ fontWeight: 500 }} className={calculated ? styles.totalValue : styles.totalValueEmpty}>
                {calculated ? finalFee(calcResult?.completionFee?.value, calcResult?.completionFee?.unit) : '0.00'}
              </span>
            </Col>
          </Row>
          {calculated && loanAmount && calcResult?.completionFee?.value && calcResult?.completionFee?.unit === 'BasisPoints' && (
            <Row justify={'space-between'} style={{ marginTop: '1rem' }}>
              <Col>
                <span style={{ fontWeight: 500 }}>{t('lender.procurationFees.labels.completionFeeBasedOnLoanAmount')}</span>
              </Col>
              <Col>
                <span style={{ fontWeight: 500 }}>
                  £{calculateFeeBasedOnLoanAmount(loanAmount, calcResult?.completionFee?.value / 10000)}
                </span>
              </Col>
            </Row>
          )}
          {calculated && !!calcResult?.trailFees?.length && (
            <>
              <Row className={styles.trailFee}>
                <Col>
                  <span style={{ fontWeight: 500 }} className={styles.totalLabel}>
                    {t('lender.procurationFees.labels.trailFee')}
                  </span>
                </Col>
              </Row>
              <Table<GetTrailFeeDto>
                className={styles.trailTable}
                size="small"
                pagination={false}
                columns={trailFeeColumns}
                dataSource={calcResult.trailFees}
                rowHoverable={false}
                rowKey={(_, index) => String(index)}
              />
            </>
          )}
        </section>
        <Button className={styles.calcBtn} type="primary" onClick={handleCalculate}>
          {calculated ? t('lender.procurationFees.buttons.recalculate') : t('lender.procurationFees.buttons.calculate')}
        </Button>
      </Spin>
    </Card>
  );
};
